import { api_configuration } from "../../../common";
import api_client_state = api_configuration.types.client_state.alan_api;
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import api_views = api_configuration.libraries.gui_definition_lib.types.views.alan_api;
import evaluateFileNameExpression from "../../state/evaluateFileNameExpression";
import { download_from_files_resource, uploadToFilesResource } from "../../interface/FilesResourceProxy";
import * as mapToIdPathBuilder from "../../state/mapToIdPathBuilder";
import { saveAs } from "./saveAs";
import { DatastoreProxy } from "../../interface/DatastoreProxy";
import { mapNodePath } from "../../state 2 interface/createPropertiesDictionaryFromColumns";
import * as JSZip from "jszip";
import { report_name_from_path } from "./reports";
import walkNodePath from "../../navigation/walkNodePath";
import { getGuiFile } from "../../guiModel";
import { IdPathBuilder } from "../../navigation/IdPathBuilder";
import { createIdPathBuilderFromNode } from "../../navigation/createIdPathFromNode";
import { setModified } from "../../state/SaveModifiedStatus";
const fileUploadQueue = new Map<api_client_state.Cyes__is_editable__file, File>();
type FileDownloadResult = [string, any] | undefined;

function containsFile(node: api_client_state.Cnode, file: api_client_state.Cfile__type__property): boolean {
	let file_parent = file.parent.parent.parent;
	while (file_parent !== node) {
		const parent = file_parent.output.position().output.parent();
		switch (parent.variant.name) {
			case "none": {
				return false;
			}
			case "node": {
				file_parent = parent.variant.definition;
			}
		}
	}
	return true;
}

export async function uploadFilesInQueue(node: api_client_state.Cnode): Promise<void> {
	const file_upload_queue = [...fileUploadQueue].filter(([file]) => {
		return containsFile(node, file.parent);
	});
	await Promise.all(
		file_upload_queue.map(async ([property, file]) => {
			await uploadFile(property, file);
		})
	);
}

export function removeFilesFromUploadQueue(node: api_client_state.Cnode): void {
	for (const [file] of fileUploadQueue) {
		if (containsFile(node, file.parent)) {
			fileUploadQueue.delete(file);
		}
	}
}

export function addFileUploadToQueue(file_property: api_client_state.Cyes__is_editable__file, file: File): void {
	const extension = file.name.substring(file.name.lastIndexOf(".") + 1);
	const is_modified = file_property.properties.modified.set("yes", {
		extension: extension,
		"upload status": ["not uploaded", {}],
	});
	setModified(is_modified);
	fileUploadQueue.set(file_property, file);
	file_property.onDestroy(() => {
		fileUploadQueue.delete(file_property);
	});
}

async function uploadFile(file_property: api_client_state.Cyes__is_editable__file, file: File) {
	switch (file_property.properties.modified.state.name) {
		case "yes": {
			const modified = file_property.properties.modified.state.node;
			modified.properties.upload_status.set("uploading", {});
			const token = await uploadToFilesResource(file);
			fileUploadQueue.delete(file_property);
			if (!file_property.destroyed) {
				modified.properties.upload_status.set("done", {
					token: token,
				});
				file_property.parent.properties.is_set.set("yes", {
					extension: modified.properties.extension,
					token: token,
				});
			}
			break;
		}
		case "no":
			break;
	}
}

export function downloadFile(file_property: api_client_state.Cfile__type__property): void {
	downloadNodeFile(file_property).then((file_download) => {
		if (file_download !== undefined) {
			saveAs(file_download[1], file_download[0]);
		}
	});
}

async function downloadNodeFile(file_property: api_client_state.Cfile__type__property): Promise<FileDownloadResult> {
	switch (file_property.properties.is_set.state.name) {
		case "no":
			return undefined;
		case "yes": {
			const set_file = file_property.properties.is_set.state.node;
			const gui_file = getGuiFile(file_property.inferences.file_type());
			if (gui_file) {
				const file_name = await evaluateFileNameExpression(
					gui_file,
					file_property.parent.parent.parent,
					set_file.properties.extension
				);
				const data = await download_from_files_resource(set_file.properties.token);
				return [file_name, data];
			} else {
				return undefined;
			}
		}
	}
}

export function downloadFiles(collection: api_client_state.Ccollection): void {
	Promise.all(
		Array.from(collection.properties.entries).map((entry) => Promise.all(downloadNodeFiles(entry[1].properties.node)))
	).then((entries) => {
		const zip = new JSZip();
		for (const entry of entries) {
			for (const file_download of entry) {
				if (file_download !== undefined) {
					zip.file(sanitizeFileName(file_download[0]), file_download[1]);
				}
			}
		}
		zip.generateAsync({ type: "blob" }).then((blob) => {
			saveAs(blob, collection.parent.parent.key.entry + ".zip");
		});
	});
}

function downloadNodeFiles(node: api_client_state.Cnode): Promise<FileDownloadResult>[] {
	const promises: Promise<FileDownloadResult>[] = [];
	for (const attribute of node.properties.attributes.values()) {
		switch (attribute.properties.type.state.name) {
			case "operation":
				break;
			case "property": {
				const property = attribute.properties.type.state.node;
				switch (property.properties.type.state.name) {
					case "collection": {
						const collection = property.properties.type.state.node;
						const application_collection = collection.inferences.collection_type();
						switch (application_collection.properties.ui.properties.size.state.name) {
							case "large":
								break;
							case "small":
								for (const entry of collection.properties.entries) {
									const entry_promises = downloadNodeFiles(entry[1].properties.node);
									for (const entry_promise of entry_promises) {
										promises.push(entry_promise);
									}
								}
								break;
						}
						break;
					}
					case "file": {
						const file = property.properties.type.state.node;
						promises.push(downloadNodeFile(file));
						break;
					}
					case "group": {
						const group = property.properties.type.state.node;
						const group_promises = downloadNodeFiles(group.properties.node);
						for (const group_promise of group_promises) {
							promises.push(group_promise);
						}
						break;
					}
					case "state group": {
						const state_group = property.properties.type.state.node;
						switch (state_group.properties.is_set.state.name) {
							case "no":
								break;
							case "yes": {
								const is_set = state_group.properties.is_set.state.node;
								const state_promises = downloadNodeFiles(is_set.properties.state.ref.properties.node);
								for (const state_promise of state_promises) {
									promises.push(state_promise);
								}
								break;
							}
						}
						break;
					}
					case "number":
						break;
					case "text":
						break;
				}
				break;
			}
		}
	}
	return promises;
}

export function downloadFileFromQuery(state: api_client_state.Cyes__is_set__file__type__cells): void {
	downloadQueryFile(state).then((data) => {
		if (data !== undefined) {
			saveAs(data[1], data[0]);
		}
	});
}

async function queryAbsoluteContext(
	context: IdPathBuilder,
	property_path: api_views.Cnode_path
): Promise<IdPathBuilder | undefined> {
	const result = await DatastoreProxy.instance.collectionQuery({
		"context node path": context.build(),
		"collection path": { "has steps": "no" },
		filters: {
			"todo filter": "no",
			"value filters": {},
		},
		"select entries": [
			"yes",
			{
				properties: {
					properties: {
						"property context": {
							path: mapNodePath(property_path).build(),
							type: "id path",
						},
					},
				},
				"limit number of entries": 1,
			},
		],
		aggregates: {},
	});

	switch (result.properties.context.state.name) {
		case "does not exist":
			return undefined;
		case "exists": {
			const $ = result.properties.context.state.node;
			const result_property = $.properties.entries
				.cast("yes")
				.properties.entries.properties.has_steps.cast("no")
				.properties.properties.properties.properties.get("property context")!;
			switch (result_property.properties.result.state.name) {
				case "does not exist":
					return undefined;
				case "exists": {
					const exists = result_property.properties.result.state.node;
					return mapToIdPathBuilder.fromClientReply(exists.properties.type.cast("id path").properties.id_path);
				}
				case "permission denied":
					return undefined;
				default:
					return unexpectedState(result_property.properties.result.state);
			}
		}
		case "permission denied":
			return undefined;
		default:
			return unexpectedState(result.properties.context.state);
	}
}

async function downloadQueryFileFromDatastore(
	context: IdPathBuilder,
	file: api_client_state.Cyes__is_set__file__type__cells
): Promise<FileDownloadResult> {
	const file_context = await queryAbsoluteContext(context, file.parent.parent.key.ref.key.ref.properties.path);
	if (file_context === undefined) {
		return undefined;
	}
	const file_name = await evaluateFileNameExpression(
		file.parent.inferences.column_type().inferences.file_type().properties.file.ref,
		file_context,
		file.properties.extension
	);
	const file_content = await download_from_files_resource(file.properties.token);
	return [file_name, file_content];
}

async function downloadQueryFile(file: api_client_state.Cyes__is_set__file__type__cells): Promise<FileDownloadResult> {
	const entry = file.parent.parent.parent;
	switch (entry.properties.entry_status.properties.initialized.state.name) {
		case "no": {
			const no = entry.properties.entry_status.properties.initialized.state.node;
			const entry_id_path = mapToIdPathBuilder.fromClientState(no.properties.referenced_node);
			return downloadQueryFileFromDatastore(entry_id_path, file);
		}
		case "yes": {
			const yes = entry.properties.entry_status.properties.initialized.state.node;
			const referenced_node = yes.properties.referenced_node.output.result_node();
			const reference_entry = referenced_node.input.entity().cast("entry");
			switch (reference_entry.properties.location.state.name) {
				case "remote": {
					const file_def = file.parent.inferences.column_type().inferences.file_type();
					const context_node = walkNodePath(file_def.parent.properties.path, referenced_node);
					if (context_node && context_node.properties.attributes.has(file_def.properties.file.entry)) {
						const node_file = context_node.properties.attributes
							.get(file_def.properties.file.entry)!
							.properties.type.cast("property")
							.properties.type.cast("file");
						switch (node_file.properties.backend_known.state.name) {
							case "no": {
								const entry_id_path = createIdPathBuilderFromNode(referenced_node);
								return downloadQueryFileFromDatastore(entry_id_path, file);
							}
							case "yes": {
								const backend_known = node_file.properties.backend_known.state.node;
								switch (backend_known.properties.is_readable.state.name) {
									case "no": {
										const entry_id_path = createIdPathBuilderFromNode(referenced_node);
										return downloadQueryFileFromDatastore(entry_id_path, file);
									}
									case "yes": {
										return downloadNodeFile(node_file);
									}
									default:
										return unexpectedState(backend_known.properties.is_readable.state);
								}
							}
							default:
								return unexpectedState(node_file.properties.backend_known.state);
						}
					} else {
						const entry_id_path = createIdPathBuilderFromNode(referenced_node);
						return downloadQueryFileFromDatastore(entry_id_path, file);
					}
				}
				case "local":
					return undefined;
				default:
					return unexpectedState(reference_entry.properties.location.state);
			}
		}
		case "unknown":
			return undefined;
		default:
			unexpectedState(entry.properties.entry_status.properties.initialized.state);
	}
}

export function downloadFilesFromQuery(query: api_client_state.Cquery__client_state): void {
	query.properties.has_result.switch({
		no: undefined,
		yes: ($) => {
			Promise.all(
				Array.from($.properties.entries).map((entry) =>
					Promise.all(
						Array.from(entry[1].properties.cells).map((cell) =>
							cell[1].properties.type.switch<Promise<FileDownloadResult>>({
								"state group": () => Promise.resolve(undefined),
								file: ($) =>
									$.properties.is_set.switch({
										no: () => Promise.resolve(undefined),
										yes: ($) => downloadQueryFile($),
									}),
								number: () => Promise.resolve(undefined),
								text: () => Promise.resolve(undefined),
								widget: () => Promise.resolve(undefined),
							})
						)
					)
				)
			).then((entries) => {
				const zip = new JSZip();
				for (const entry of entries) {
					for (const file_download of entry) {
						if (file_download !== undefined) {
							zip.file(sanitizeFileName(file_download[0]), file_download[1]);
						}
					}
				}
				zip.generateAsync({ type: "blob" }).then((blob) => {
					const file_name = query.properties.definition.ref.properties.context.switch({
						candidates: "download",
						node: ($) => report_name_from_path($.properties.path),
					});
					saveAs(blob, file_name + ".zip");
				});
			});
		},
	});
}

export function canDownloadFiles(collection: api_app.Ccollection__type__property): boolean {
	const can_download = collection as unknown as { can_download_files: boolean | undefined };
	if (can_download.can_download_files === undefined) {
		can_download.can_download_files = canDownloadFilesNode(collection.properties.node);
	}
	return can_download.can_download_files;
}

export function canDownloadFilesQuery(query: api_views.Cqueries): boolean {
	const can_download = query as unknown as { can_download_files: boolean | undefined };
	if (can_download.can_download_files === undefined) {
		let can_download_files = false;
		for (const column of query.properties.columns.values()) {
			switch (column.properties.type.state.name) {
				case "file":
					can_download_files = true;
					break;
				case "number":
					break;
				case "state group":
					break;
				case "text":
					break;
				case "widget":
					break;
			}
		}
		can_download.can_download_files = can_download_files;
	}
	return can_download.can_download_files;
}

const nodeHasFileChildren = new Map<api_app.Cnode__application, boolean>();

function canDownloadFilesNode(node: api_app.Cnode__application): boolean {
	if (!nodeHasFileChildren.has(node)) {
		let has_files = false;
		for (const attribute of node.properties.attributes.values()) {
			const $type = attribute.properties.type.state;
			switch ($type.name) {
				case "action":
					break;
				case "command":
					break;
				case "reference set":
					break;
				case "property":
					switch ($type.node.properties.type.state.name) {
						case "text":
							break;
						case "number":
							break;
						case "file":
							has_files = true;
							break;
						case "group":
							if (!has_files) {
								has_files = canDownloadFilesNode($type.node.properties.type.state.node.properties.node);
							}
							break;
						case "state group":
							if (!has_files) {
								for (const state of $type.node.properties.type.state.node.properties.states.values()) {
									if (!has_files) {
										has_files = canDownloadFilesNode(state.properties.node);
									}
								}
							}
							break;
						case "collection": {
							const collection = $type.node.properties.type.state.node;
							if (!has_files) {
								const size = collection.properties.ui.properties.size;
								switch (size.state.name) {
									case "large":
										break;
									case "small":
										has_files = canDownloadFilesNode(collection.properties.node);
										break;
								}
							}
							break;
						}
					}
					break;
			}
		}
		nodeHasFileChildren.set(node, has_files);
	}
	return nodeHasFileChildren.get(node)!;
}

export function removeFileFromQueue(file: api_client_state.Cfile__type__property): void {
	switch (file.properties.is_editable.state.name) {
		case "yes": {
			fileUploadQueue.delete(file.properties.is_editable.state.node);
			break;
		}
		case "no":
			break;
	}
}

// eslint-disable-next-line no-useless-escape
const illegalRe = /[\/\?<>\\:\*\|"]/g;
// eslint-disable-next-line no-control-regex
const controlRe = /[\x00-\x1f\x80-\x9f]/g;
const reservedRe = /^\.+$/;
const windowsReservedRe = /^(con|prn|aux|nul|com[0-9]|lpt[0-9])(\..*)?$/i;
const windowsTrailingRe = /[. ]+$/;
const linuxPathSeparator = /\//g;

//from: https://github.com/parshap/node-sanitize-filename/blob/master/index.js
function sanitizeFileName(name: string, replacement = "_"): string {
	return name
		.replace(illegalRe, replacement)
		.replace(controlRe, replacement)
		.replace(reservedRe, replacement)
		.replace(windowsReservedRe, replacement)
		.replace(windowsTrailingRe, replacement)
		.replace(linuxPathSeparator, replacement);
}
